import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { roleHome } from "../components/ProtectedRoute";
import { getInitialTheme, applyTheme } from "../lib/theme";
import api, { formatApiError } from "../lib/api";
import { passwordPolicyError, PASSWORD_HELP_TEXT } from "../lib/passwordPolicy";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff, LogOut, ShieldCheck } from "lucide-react";
import { PRODUCT_NAME, PLATFORM_COMPANY_NAME } from "../lib/platformCompany";

// Fluxo Obrigatório de Troca de Senha no Primeiro Acesso — tela intermediária
// forçada quando o usuário autenticado tem must_change_password=true (senha
// provisória, criada pelo Admin/Admin Master ou por um reset administrativo).
// O bloqueio de verdade é do BACKEND (get_current_user, ver H5-003-01) —
// esta tela é só a experiência correta para uma regra que já existe no
// servidor: nenhum módulo operacional (menu, Dashboard, Clientes, Fichas...)
// é renderizado enquanto a troca estiver pendente. Deliberadamente sem
// identidade visual do tenant (mesma família de Login/ForgotPassword/
// ResetPassword, que também nunca usam branding do tenant) — evita depender
// de GET /branding, que fica fora da allowlist mínima do backend enquanto a
// troca está pendente (ver MUST_CHANGE_PASSWORD_ALLOWED_PATHS). Usa os
// tokens de tema (bg-card, bg-primary...) para respeitar claro/escuro.
export default function PrimeiroAcesso() {
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // Preferência de tema já é por-sessão-de-navegador (localStorage) e está
  // disponível aqui — o usuário já está autenticado, só ainda não pode ver
  // o menu operacional. Ver lib/theme.js.
  useEffect(() => { applyTheme(getInitialTheme()); }, []);

  if (user === null) {
    return <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">Carregando...</div>;
  }
  if (user === false) return <Navigate to="/login" replace />;
  if (!user.must_change_password) return <Navigate to={roleHome(user.role)} replace />;
  // Verificação por Código — sem o código confirmado, nem chega a ver esta
  // tela (evita um formulário que o backend vai recusar de qualquer forma —
  // ver change_password/_first_access_grant).
  if (!user.otp_verified) return <Navigate to="/verificar-codigo" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!pw || !pw2) { setErr("Preencha os dois campos."); return; }
    const pwErr = passwordPolicyError(pw);
    if (pwErr) { setErr(pwErr); return; }
    if (pw !== pw2) { setErr("As senhas não coincidem."); return; }
    setBusy(true);
    try {
      // Só os 2 campos pedidos — sem reenviar a senha provisória, que o
      // usuário já usou para autenticar (ver change_password no backend:
      // dispensa current_password quando must_change_password=true).
      // Hardening pós-Red Team Fase 1 (SEC-AUTH-001) — troca de senha agora
      // invalida sessões anteriores no servidor e reemite um token novo; sem
      // atualizar o localStorage aqui, chamadas que dependem do fallback
      // Authorization: Bearer (quando o cookie não estiver disponível)
      // ficariam usando um token já invalidado.
      const { data } = await api.post("/auth/change-password", { new_password: pw });
      if (data.token) localStorage.setItem("token", data.token);
      setUser(u => (u ? { ...u, must_change_password: false } : u));
      toast.success("Senha alterada com sucesso");
      nav(roleHome(user.role), { replace: true });
    } catch (ex) {
      if (ex?.response?.status === 401) {
        setErr("Sua sessão expirou. Faça login novamente.");
      } else if (ex?.response?.status === 403) {
        // grant do OTP expirou entre a confirmação e agora — volta para a
        // verificação em vez de deixar o usuário preso num erro genérico.
        setUser(u => (u ? { ...u, otp_verified: false } : u));
        nav("/verificar-codigo", { replace: true });
        toast.error("A verificação expirou. Confirme o código novamente.");
      } else {
        setErr(formatApiError(ex));
      }
    } finally {
      setBusy(false);
    }
  };

  const sair = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 sm:p-6">
      <Card className="w-full max-w-sm p-6 sm:p-8" data-testid="primeiro-acesso-form">
        <div className="text-center">
          <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center font-heading font-black text-2xl text-white bg-primary">L</div>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground">Defina sua nova senha</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Por segurança, você precisa criar uma nova senha antes de continuar.
          </p>
        </div>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="pa-pw">Nova senha</Label>
            <div className="relative mt-1">
              <Input
                id="pa-pw" type={showPw ? "text" : "password"} value={pw}
                onChange={e => setPw(e.target.value)} required autoFocus
                className="pr-10" data-testid="primeiro-acesso-pw"
                autoComplete="new-password"
              />
              <button
                type="button" onClick={() => setShowPw(s => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label={showPw ? "Ocultar senha" : "Mostrar senha"}
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">{PASSWORD_HELP_TEXT}</p>
          </div>
          <div>
            <Label htmlFor="pa-pw2">Confirmar nova senha</Label>
            <Input
              id="pa-pw2" type={showPw ? "text" : "password"} value={pw2}
              onChange={e => setPw2(e.target.value)} required
              className="mt-1" data-testid="primeiro-acesso-pw2"
              autoComplete="new-password"
            />
          </div>
          {err && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 dark:bg-red-950 dark:text-red-300 dark:border-red-900" data-testid="primeiro-acesso-error">
              {err}
            </div>
          )}
          <Button type="submit" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" disabled={busy} data-testid="primeiro-acesso-submit">
            <KeyRound size={16} /> {busy ? "Salvando..." : "Alterar senha e continuar"}
          </Button>
          <Button type="button" variant="outline" className="w-full" onClick={sair} disabled={busy} data-testid="primeiro-acesso-sair">
            <LogOut size={16} /> Sair
          </Button>
        </form>
        <div className="mt-6 pt-4 border-t border-border flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <ShieldCheck size={12} /> {PRODUCT_NAME} — {PLATFORM_COMPANY_NAME}
        </div>
      </Card>
    </div>
  );
}
